/**
 * F-001 — Walking skeleton bootstrap.
 *
 * Wires a Three.js renderer to a host canvas, sets up a placeholder camera
 * looking at the cosmic background, and starts a render loop. Later phases
 * (F-002+) replace the camera, add lighting, and introduce the playfield.
 *
 * The factory pattern keeps the bootstrap testable: callers (main.ts,
 * tests) inject a canvas + size, and the function returns the live handles
 * needed for teardown.
 */

import {
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  type Object3D,
} from "three";

import { createCosmicBackground } from "./background.ts";
import { createSceneLights, type SceneLights } from "./lighting.ts";
import { createPlayfield, type Playfield } from "./playfield.ts";

export interface BootstrapOptions {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  /** Override for headless environments where WebGL is not available. */
  rendererFactory?: (canvas: HTMLCanvasElement) => WebGLRenderer;
  /** F-003 — show wireframe playfield walls + limit ribbon. Default false. */
  debugPlayfield?: boolean;
}

export interface BootstrapHandles {
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  background: Object3D;
  lights: SceneLights;
  playfield: Playfield;
  dispose: () => void;
}

const DEFAULT_FOV = 60;
const DEFAULT_NEAR = 0.1;
const DEFAULT_FAR = 1_000;
const CAMERA_DISTANCE = 18;

export function bootstrapScene(options: BootstrapOptions): BootstrapHandles {
  const { canvas, width, height } = options;
  if (width <= 0 || height <= 0) {
    throw new Error(`bootstrapScene: invalid size ${width}x${height}`);
  }

  const scene = new Scene();
  const camera = new PerspectiveCamera(
    DEFAULT_FOV,
    width / height,
    DEFAULT_NEAR,
    DEFAULT_FAR,
  );
  camera.position.set(0, 0, CAMERA_DISTANCE);
  camera.lookAt(0, 0, 0);

  const renderer = options.rendererFactory
    ? options.rendererFactory(canvas)
    : new WebGLRenderer({ canvas, antialias: true });
  renderer.setSize(width, height, false);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio ?? 1, 2));

  const background = createCosmicBackground();
  scene.add(background);

  const lights = createSceneLights();
  scene.add(lights.ambient);
  scene.add(lights.sun);
  scene.add(lights.sun.target);

  const playfield = createPlayfield({ debugVisible: options.debugPlayfield ?? false });
  scene.add(playfield.group);

  const dispose = (): void => {
    renderer.dispose();
    scene.remove(background);
    for (const light of lights.all) {
      scene.remove(light);
    }
    scene.remove(lights.sun.target);
    scene.remove(playfield.group);
  };

  return { scene, camera, renderer, background, lights, playfield, dispose };
}

export const __testing = {
  DEFAULT_FOV,
  DEFAULT_NEAR,
  DEFAULT_FAR,
  CAMERA_DISTANCE,
};
